import PropTypes from 'prop-types';
import React, { useRef, useContext } from 'react';
import { Input, InputNumber } from 'antd';
import Form from 'components/Form';
import SearchAddress from 'components/SearchAddress';
import { AsyncSelect } from 'components/Select';
import FormItem from 'components/FormItem';
import { US_COUNTRY_CODES } from 'components/PlaceAutoComplete/PlaceAutoComplete';
import DisabledContext from 'antd/lib/config-provider/DisabledContext';

export default function AddressFields({
  searchRef,
  addressKey,
  formListKey,
  required,
  disableFields,
  readOnly = false,
  disabled = false,
  labels: _labels,
  ...rest
}) {
  const localRef = useRef(null);
  const inputRef = searchRef || localRef;
  const form = Form.useFormInstance();
  const isArray = Array.isArray(addressKey);

  // Detect form disabled state
  const isFormDisabled = useContext(DisabledContext);
  const isDisabled = disabled || disableFields || isFormDisabled;

  const id = isArray ? [...addressKey, 'id'] : [addressKey, 'id'];
  const street = isArray ? [...addressKey, 'street'] : [addressKey, 'street'];
  const street2 = isArray ? [...addressKey, 'street2'] : [addressKey, 'street2'];
  const state = isArray ? [...addressKey, 'state'] : [addressKey, 'state'];
  const city = isArray ? [...addressKey, 'city'] : [addressKey, 'city'];
  const postalCode = isArray ? [...addressKey, 'postalCode'] : [addressKey, 'postalCode'];
  const county = isArray ? [...addressKey, 'county'] : [addressKey, 'county'];
  const lat = isArray ? [...addressKey, 'location', 'lat'] : [addressKey, 'location', 'lat'];
  const lon = isArray ? [...addressKey, 'location', 'lat'] : [addressKey, 'location', 'lon'];
  const description = isArray ? [...addressKey, 'description'] : [addressKey, 'description'];
  const placeId = isArray ? [...addressKey, 'placeId'] : [addressKey, 'placeId'];

  const labels = {
    ...AddressFields.defaultProps.labels,
    ..._labels,
  };

  return (
    <div className={rest?.['className']} data-testid={rest['data-testid']}>
      <FormItem name={id} hidden>
        <InputNumber />
      </FormItem>

      <FormItem
        name={street}
        label={labels?.street}
        rules={disabled || readOnly ? [] : [{ required: required?.street, message: 'Street address is required' }]}
        className="max-w-[unset]"
      >
        <SearchAddress
          ref={inputRef}
          country={US_COUNTRY_CODES}
          label={labels?.street}
          onSelect={(location) => {
            const _updatePath = isArray && formListKey ? [formListKey, ...addressKey] : addressKey;
            form.setFieldValue(_updatePath, {
              ...location,
              postalCode: location?.zipCode,
              street2: '',
              location: {
                lat: location?.lat,
                lon: location?.lng,
              },
            });
            form.validateFields(
              ['country', 'city', 'state', 'postalCode'].map((name) => [
                isArray ? addressKey.join('_') : addressKey,
                name,
              ]),
            );
            inputRef.current.update(location?.street || '');
          }}
          disabled={isDisabled}
          readOnly={readOnly}
          data-testid="addressLine1"
        />
      </FormItem>

      <FormItem name={street2} label={labels?.street2} className="max-w-[unset]">
        <Input disabled={isDisabled} readOnly={readOnly} data-testid="addressLine2" />
      </FormItem>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FormItem
          name={city}
          label={labels?.city}
          rules={disabled || readOnly ? [] : [{ required: required?.city, message: 'City is required' }]}
        >
          <Input disabled={isDisabled} readOnly={readOnly} data-testid="city" />
        </FormItem>

        <FormItem
          name={state}
          label={labels?.state}
          rules={disabled || readOnly ? [] : [{ required: required?.state, message: 'State is required' }]}
        >
          <AsyncSelect
            name="mailingAddress.state"
            placeholder="Select..."
            ariaLabel="State"
            disabled={isDisabled}
            optionsApiUrl="/countries/states/US"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.code}
            isOptionSelected={(option, [value]) => option?.code === value}
            onChangeFormatter={(v) => v?.code}
            readOnly={readOnly}
          />
        </FormItem>

        <FormItem
          name={postalCode}
          label={labels?.postalCode}
          rules={
            disabled || readOnly
              ? []
              : [
                  { required: required?.postalCode, message: 'ZIP code is required' },
                  {
                    pattern: /^\d{5}(-\d{4})?$/,
                    message: 'Please enter a valid US zip code',
                  },
                ]
          }
        >
          <Input disabled={isDisabled} readOnly={readOnly} data-testid="postalCode" />
        </FormItem>
      </div>

      <Form.Item name={county} hidden>
        <Input />
      </Form.Item>

      <Form.Item label="Latitude" name={lat} hidden>
        <InputNumber disabled={isDisabled} />
      </Form.Item>

      <Form.Item label="Longitude" name={lon} hidden>
        <InputNumber disabled={isDisabled} />
      </Form.Item>

      <Form.Item name={description} hidden>
        <Input />
      </Form.Item>

      <Form.Item name={placeId} hidden>
        <Input />
      </Form.Item>

      <Form.Item name={id} hidden>
        <InputNumber />
      </Form.Item>
    </div>
  );
}

AddressFields.propTypes = {
  addressKey: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  searchRef: PropTypes.any,
  showLatLng: PropTypes.bool,
  disableFields: PropTypes.bool,
  required: PropTypes.object,
  labels: PropTypes.object,
};

AddressFields.defaultProps = {
  showSearch: true,
  showLatLng: false,
  disableFields: false,
  required: {
    country: true,
    city: true,
    state: true,
    postalCode: true,
    street: true,
  },
  searchCols: {
    xs: 24,
    sm: 12,
    md: 8,
  },
  labels: {
    street: 'Street Address',
    street2: 'Apartment/Unit (optional)',
    city: 'City',
    state: 'State',
    postalCode: 'Zip Code',
  },
};
